import { AbstractControl, NgControl, ValidationErrors } from '@angular/forms';

/**
 * Standard required field validation Used by all form controls to validate required constraint
 *
 * @example
 *   ```typescript
 *   validate(control: AbstractControl): ValidationErrors | null {
 *     return validateRequired(this.required(), this._hasValue());
 *   }
 *   ```;
 *
 * @param required - Whether field is required
 * @param hasValue - Whether field has a value
 * @returns ValidationErrors with 'required' key, or null if valid
 */
export function validateRequired(required: boolean, hasValue: boolean): ValidationErrors | null {
  if (required && !hasValue) {
    return { required: true };
  }
  return null;
}

/**
 * Detect error state from ngControl Checks if control is invalid AND (dirty OR touched) Standard pattern for all form
 * controls
 *
 * @example
 *   ```typescript
 *   protected readonly isInErrorState = computed(() => {
 *     return isInErrorState(this.ngControl);
 *   });
 *   ```;
 *
 * @param ngControl - NgControl instance (optional)
 * @returns True if control is in error state
 */
export function isInErrorState(ngControl: NgControl | null): boolean {
  if (!ngControl) {
    return false;
  }
  const control = ngControl.control;
  return !!(control && control.invalid && (control.dirty || control.touched));
}

/**
 * Combine multiple validation error objects Useful when applying multiple validators
 *
 * @example
 *   ```typescript
 *   validate(control: AbstractControl): ValidationErrors | null {
 *     return mergeValidationErrors(
 *       validateRequired(this.required(), this._hasValue()),
 *       this._validateCustom(control)
 *     );
 *   }
 *   ```;
 *
 * @param errors - Variable number of ValidationErrors or null
 * @returns Merged ValidationErrors or null if no errors
 */
export function mergeValidationErrors(...errors: (ValidationErrors | null)[]): ValidationErrors | null {
  const merged = errors.filter((e) => e !== null).reduce((acc, e) => ({ ...acc, ...e }), {} as ValidationErrors);

  return Object.keys(merged).length > 0 ? merged : null;
}

/**
 * Check if control has specific validation error
 *
 * @example
 *   ```typescript
 *   if (hasValidationError(this.ngControl?.control, 'required')) {
 *     // Show required error message
 *   }
 *   ```;
 *
 * @param control - AbstractControl to check
 * @param errorKey - Name of error to check (e.g., 'required', 'email')
 * @returns True if control has error with specified key
 */
export function hasValidationError(control: AbstractControl | null | undefined, errorKey: string): boolean {
  return !!(control && control.hasError(errorKey));
}

/**
 * Get validation error value Some validators return error objects with additional info
 *
 * @example
 *   ```typescript
 *   const minlengthError = getValidationError(control, 'minlength');
 *   if (minlengthError) {
 *     // Show: "Must be at least X characters"
 *   }
 *   ```;
 *
 * @param control - AbstractControl to check
 * @param errorKey - Name of error to retrieve
 * @returns Error value or null if not present
 */
export function getValidationError(control: AbstractControl | null | undefined, errorKey: string): any {
  return control?.getError(errorKey);
}
